import {
  getAccessToken as getValidAccessToken,
  IConfig,
} from 'redux-jwt-protected-middleware'
import { clearSession, updateSession } from './actions'
import { isAuthenticated } from './selectors'
import { getBearerToken } from './getBearerToken'
import { getRefreshToken } from './getRefreshToken'
import { refreshCurrentAccessToken } from './refreshCurrentAccessToken'
import { Store, AnyAction } from 'redux'
import { IStoreState } from '../store'

const generateConfig = (apiUrl: string): IConfig => {
  console.log(
    'DEBUG: fetchValidatedToken - Generating config with API URL:',
    apiUrl
  )

  // Check if access token and refresh token are the same and warn if they are
  const accessToken = getBearerToken()
  console.log(
    'DEBUG: fetchValidatedToken - Current access token present:',
    !!accessToken
  )

  const refreshToken = getRefreshToken()
  console.log(
    'DEBUG: fetchValidatedToken - Current refresh token present:',
    !!refreshToken
  )

  if (accessToken && refreshToken && accessToken === refreshToken) {
    console.log(
      'WARNING: Access token and refresh token are identical. This is a security risk and may prevent proper token refresh.'
    )
  }

  return {
    currentAccessToken: () => {
      const token = getBearerToken()
      console.log(
        'DEBUG: fetchValidatedToken - currentAccessToken called, token present:',
        !!token
      )
      return token
    },
    currentRefreshToken: () => {
      const token = getRefreshToken()
      console.log(
        'DEBUG: fetchValidatedToken - currentRefreshToken called, token present:',
        !!token
      )
      return token
    },
    handleAuthenticationError: (
      e: Error,
      store: Store<IStoreState, AnyAction>
    ) => {
      console.log(
        'DEBUG: fetchValidatedToken - handleAuthenticationError called with error:',
        e
      )
      console.trace('DEBUG: Stack trace for authentication error')

      if (!isAuthenticated(store.getState())) {
        console.log(
          'DEBUG: fetchValidatedToken - Not authenticated, clearing session with invalidSession=false'
        )
        store.dispatch(clearSession({ invalidSession: false }))
        return ''
      }

      console.log(
        'DEBUG: fetchValidatedToken - Could not refresh token, setting invalidSession=true'
      )
      console.log(e)
      store.dispatch(clearSession({ invalidSession: true }))
      return ''
    },
    handleRefreshAccessToken: async (
      refreshToken: string,
      store: Store<IStoreState, AnyAction>
    ) => {
      console.log(
        'DEBUG: fetchValidatedToken - handleRefreshAccessToken called'
      )

      try {
        // Verify that refreshToken is not empty or undefined
        if (!refreshToken) {
          console.log(
            'DEBUG: fetchValidatedToken - No refresh token available for token refresh'
          )
          console.log(
            'DEBUG: fetchValidatedToken - Setting invalidSession=true due to missing refresh token'
          )
          store.dispatch(clearSession({ invalidSession: true }))
          return ''
        }

        // Log the refresh token being used (redacted for security)
        console.log(
          'DEBUG: fetchValidatedToken - Using refresh token for refresh: ' +
            refreshToken.substring(0, 5) +
            '...'
        )

        console.log(
          'DEBUG: fetchValidatedToken - Calling refreshCurrentAccessToken with API URL:',
          apiUrl
        )
        const response = await refreshCurrentAccessToken(apiUrl, refreshToken)
        console.log(
          'DEBUG: fetchValidatedToken - Response received from token refresh'
        )

        if (response.errors && Object.keys(response.errors).length > 0) {
          console.log(
            'DEBUG: fetchValidatedToken - Error refreshing token:',
            response.errors
          )
          console.log(
            'DEBUG: fetchValidatedToken - Setting invalidSession=true due to refresh errors'
          )
          store.dispatch(clearSession({ invalidSession: true }))
          return ''
        }

        // Check if the new tokens are valid
        if (!response.access_token || !response.refresh_token) {
          console.log(
            'DEBUG: fetchValidatedToken - Invalid token response received:',
            JSON.stringify({
              access_token_present: !!response.access_token,
              refresh_token_present: !!response.refresh_token,
            })
          )
          console.log(
            'DEBUG: fetchValidatedToken - Setting invalidSession=true due to missing tokens in response'
          )
          store.dispatch(clearSession({ invalidSession: true }))
          return ''
        }

        // Check if new tokens are identical (this would be a problem)
        if (response.access_token === response.refresh_token) {
          console.log(
            'WARNING: New access token and refresh token are identical. This is a security risk.'
          )
        }

        console.log('DEBUG: fetchValidatedToken - Token refresh successful')
        console.log(
          'DEBUG: fetchValidatedToken - Updated refresh token length:',
          response.refresh_token.length
        )

        console.log(
          'DEBUG: fetchValidatedToken - Updating session with new tokens'
        )
        store.dispatch(
          updateSession(
            {
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
            },
            // Preserve existing onboarding flags during token refresh
            {
              onboardingRequired: store.getState().session.onboardingRequired,
              skippedOnboarding: store.getState().session.skippedOnboarding,
            }
          )
        )
        return response.access_token
      } catch (e) {
        console.log(
          'DEBUG: fetchValidatedToken - Error during token refresh:',
          e
        )
        console.trace('DEBUG: Stack trace for token refresh error')
        console.log(
          'DEBUG: fetchValidatedToken - Setting invalidSession=true due to refresh exception'
        )
        store.dispatch(clearSession({ invalidSession: true }))
        return ''
      }
    },
  }
}

/**
 * Refreshes the current access token via the session store
 * of our redux application.
 */
export const fetchValidatedToken = (apiUrl: string) => {
  console.log(
    'DEBUG: fetchValidatedToken function called with API URL:',
    apiUrl
  )
  const tokenFn = getValidAccessToken(generateConfig(apiUrl))

  return (store: Store<IStoreState, AnyAction>) => {
    console.log(
      'DEBUG: fetchValidatedToken - Returning wrapped function with store:',
      !!store
    )
    return tokenFn(store)
  }
}
